import { NODE_TYPES } from '@/entities/user';
import { useAuth } from '@/features/authentication';
import { LogoIcon } from '@/shared/brand';
import { CheckCircle2, Loader2, Shield, Star } from 'lucide-react';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

/// Layout for the auth route group. Redirects already-authenticated users to
/// their home: /admin for super-admins, /dashboard otherwise. (React Router
/// port of the Next.js (auth)/layout.tsx.)
///
/// The target must match the post-login redirect in the login page: this
/// effect fires as soon as `user` is set, racing the login page's own
/// navigate(). If they disagree, an admin can land on /dashboard.
///
/// Accounts still signing up (node type 2, phone/photo missing) stay on
/// /register to finish; every other auth page sends them back there.
export function AuthLayout() {
  const { isAuthenticated, isAdmin, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPendingRegistration = user?.nodeTypeId === NODE_TYPES.PENDING_VALIDATION;
  const isFinishingRegistration = isPendingRegistration && location.pathname === '/register';

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (isPendingRegistration) {
      if (location.pathname !== '/register') navigate('/register', { replace: true });
      return;
    }
    navigate(isAdmin ? '/admin' : '/dashboard', { replace: true });
  }, [isAuthenticated, isAdmin, isLoading, isPendingRegistration, location.pathname, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <LogoIcon size={48} />
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isAuthenticated && !isFinishingRegistration) {
    return null;
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side - branding with elegant teal gradient */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-dark" />
        <div className="absolute inset-0 pattern-geometric opacity-10" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full border-[40px] border-white/10" />
          <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full border-[30px] border-white/10" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 rounded-full border-[20px] border-white/10" />
        </div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="max-w-md text-center text-primary-foreground">
            <div className="flex items-center justify-center mb-8">
              <LogoIcon size={80} variant="light" className="shadow-xl" />
            </div>
            <h1 className="font-display text-4xl font-semibold mb-4">Zawajme</h1>
            <h2 className="text-2xl font-medium mb-4 opacity-95">
              Trouvez votre partenaire de vie
            </h2>
            <p className="text-lg opacity-85 leading-relaxed">
              La plateforme de confiance pour ceux qui recherchent une relation sérieuse menant au
              mariage.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-display font-semibold">50K+</div>
                <div className="text-sm opacity-80 mt-1">Membres vérifiés</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-display font-semibold">10K+</div>
                <div className="text-sm opacity-80 mt-1">Mariages</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-display font-semibold">95%</div>
                <div className="text-sm opacity-80 mt-1">Satisfaction</div>
              </div>
            </div>
            <div className="mt-12 flex flex-col gap-3">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3">
                <Shield className="h-5 w-5" />
                <span className="text-sm">Profils vérifiés manuellement</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm">Données chiffrées et sécurisées</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3">
                <Star className="h-5 w-5" />
                <span className="text-sm">Service premium depuis 2015</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - auth form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 sm:p-8 bg-background pattern-geometric">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden flex-col items-center justify-center mb-8">
            <LogoIcon size={56} className="shadow-[var(--shadow-primary)] mb-3" />
            <span className="font-display text-xl font-semibold">Zawajme</span>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
